// Kanban (RF-UI-13, RF-FLOW-01..03, PRD §7): nine columns plus a side lane for side
// states. Dragging a card only *requests* an action; the server decides and any refusal
// (gate_pending, dependencies, version conflict) is shown with its reason. Every card has
// an "Actions" menu as the keyboard/pointer-free alternative to dragging. Cards in a working
// column show the agent and effective model of the current attempt plus a live "working for"
// timer; every card shows how long it has been in its column (or since it finished).
import { useEffect, useMemo, useState, type DragEvent } from "react";
import { useSearchParams } from "react-router";
import { RISK_LEVELS, TASK_COLUMNS, TASK_ORIGINS, TASK_SIDE_STATES, TASK_TYPES } from "@maestros/contracts/enums";
import type { Board, ProjectSummary, TaskAction, TaskCard, TaskStatus } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getTask, taskAction } from "../../api/endpoints.ts";
import { isApiError } from "../../api/client.ts";
import { useDebouncedCallback, type AsyncState } from "../../lib/useAsync.ts";
import { useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { RISK_VIEW, TASK_STATUS_VIEW, statusView } from "../../lib/status.ts";
import { Button } from "../../components/Button.tsx";
import { Banner, EmptyState, ErrorBanner, LoadingBlock, localErrorMessage } from "../../components/Feedback.tsx";
import { Menu } from "../../components/Menu.tsx";
import { StatusIcon, StatusPill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { TextAreaField } from "../../components/Field.tsx";
import { FileTextIcon, KanbanIcon, LinkIcon, MoreIcon, PlusIcon, SearchIcon } from "../../components/icons.tsx";
import { CreateTaskDialog } from "../../features/CreateTaskDialog.tsx";
import { actionForMove, candidateActions } from "./taskActions.ts";
import { TaskDetailDialog } from "./TaskDetailDialog.tsx";
import { CardExecution, CardTimers, ScoreChip } from "./TaskCardFacts.tsx";
import { AwaitingHumanColumn } from "../../features/wave5/ApprovalColumn.tsx";
import { boardTasks, startedDependents, StartedDependentsList } from "../../features/onboarding/StartedDependents.tsx";
import { TranslateTasks } from "../../features/content-language/TranslateTasks.tsx";
import { BlockPanel } from "../../features/blocks/BlockPanel.tsx";

/** Cards shown per Kanban column before "Load +N/remaining". */
export const KANBAN_PAGE_SIZE = 20;

/**
 * Execution events that change what a card shows (new attempt, model reported by the router).
 * task.* events are already refreshed by ProjectPage; these complete the live card facts.
 */
export const CARD_REFRESH_EVENTS: ReadonlySet<string> = new Set([
  "run.started",
  "model.route.selected",
  "model.escalation.started",
  "model.call.started",
  "model.call.finished",
  "model.call.interrupted",
  "quota.wait.started",
  "quota.wait.released",
]);

interface Refusal {
  taskNumber: number;
  action: TaskAction;
  code: string;
  message: string;
  pendingGates: string[];
  correlationId: string | null;
}

interface Filters {
  search: string;
  risk: string;
  type: string;
  origin: string;
  prd: string;
}

function pendingGatesOf(details: Record<string, unknown> | undefined): string[] {
  const raw = details?.["pending_gates"] ?? details?.["pendingGates"];
  return Array.isArray(raw) ? raw.filter((g): g is string => typeof g === "string") : [];
}

function matches(task: TaskCard, f: Filters): boolean {
  if (f.risk && task.risk !== f.risk) return false;
  if (f.type && task.type !== f.type) return false;
  if (f.origin && task.origin !== f.origin) return false;
  if (f.prd && task.prd_version_id !== f.prd) return false;
  if (f.search) {
    const q = f.search.toLowerCase();
    if (!task.title.toLowerCase().includes(q) && !`#${task.number}`.includes(q)) return false;
  }
  return true;
}

function BoardCard({
  task,
  numbers,
  busy,
  onAction,
  onOpen,
  onDragStart,
  serverActions,
  onMenuOpen,
  onChanged,
  startPending,
}: {
  startPending: boolean;
  serverActions: TaskAction[] | null;
  /** A decision on the block was applied: the board reloads. */
  onChanged: () => void;
  onMenuOpen: (task: TaskCard) => void;
  task: TaskCard;
  numbers: Map<string, number>;
  busy: boolean;
  onAction: (task: TaskCard, action: TaskAction) => void;
  onOpen: (task: TaskCard) => void;
  onDragStart: (task: TaskCard, e: DragEvent<HTMLElement>) => void;
}) {
  const { t } = useI18n();
  const actions = candidateActions(task, serverActions);
  const deps = task.depends_on.map((id) => numbers.get(id)).filter((n): n is number => n !== undefined);
  const risk = RISK_VIEW[task.risk];
  return (
    <li
      className={`task-card${busy ? " is-busy" : ""}`}
      draggable={!busy}
      onDragStart={(e) => onDragStart(task, e)}
      data-task-id={task.id}
      data-testid={`task-card-${task.number}`}
      aria-busy={busy || undefined}
    >
      <div className="task-card-head">
        <span className="mono subtle">#{task.number}</span>
        <StatusPill view={statusView(task.status)} />
      </div>
      <button type="button" className="task-card-title" onClick={() => onOpen(task)}>
        <span className="user-content">{task.title}</span>
      </button>
      <CardExecution task={task} />
      {startPending && task.status === "todo" && !task.current_attempt ? <p className="subtle" data-testid="card-start-pending">{t("kanban.startPending")}</p> : null}
      <CardTimers task={task} />
      <div className="task-card-meta">
        {risk ? (
          <span className={`badge tone-${risk.tone}`}>
            <StatusIcon kind={risk.icon} size={12} />
            {t(risk.label)}
          </span>
        ) : null}
        <span className="badge">{t(`taskType.${task.type}`)}</span>
        {task.priority_score !== null ? <ScoreChip score={task.priority_score} reason={task.priority_reason} /> : null}
        {task.prd_version_id ? (
          <span className="badge">
            <FileTextIcon size={12} />
            {task.requirement_refs.length > 0 ? task.requirement_refs.slice(0, 2).join(", ") : t("task.fromPrd")}
          </span>
        ) : null}
      </div>
      {deps.length > 0 || task.depends_on.length > 0 ? (
        <p className="task-card-deps">
          <LinkIcon size={12} />
          {t("task.dependsOn", { list: deps.length > 0 ? deps.map((n) => `#${n}`).join(", ") : String(task.depends_on.length) })}
        </p>
      ) : null}
      {task.status === "blocked" ? (
        // D37: translated title, explanation, what waits and the top decisions; "See what to do" opens the modal.
        <BlockPanel task={task} compact onChanged={onChanged} onOpenDetails={() => onOpen(task)} />
      ) : task.status_reason ? (
        <p className="task-card-blocker">{task.status_reason}</p>
      ) : null}
      {task.return_column ? <p className="task-card-return subtle">{t("task.returnsTo", { column: t(TASK_STATUS_VIEW[task.return_column].label) })}</p> : null}
      <div className="task-card-foot">
        <span className="subtle">{t(`delivery.${task.delivery_target}`)}</span>
        <Menu
          label={t("kanban.actionsFor", { number: task.number })}
          onOpen={() => onMenuOpen(task)}
          items={
            actions.length > 0
              ? [
                  { id: "label", kind: "label", label: serverActions ? t("kanban.requestAction") : t("kanban.suggestedActions") },
                  ...actions.map((a) => ({ id: a, label: t(`action.${a}`), onSelect: () => onAction(task, a) })),
                ]
              : [{ id: "none", label: t("kanban.noActions"), disabled: true }]
          }
          trigger={(p) => (
            <button type="button" className="icon-btn task-card-menu" {...p} aria-label={t("kanban.actionsFor", { number: task.number })} disabled={busy}>
              <MoreIcon />
            </button>
          )}
        />
      </div>
    </li>
  );
}

export function KanbanTab({ project, board, onProjectChanged }: { project: ProjectSummary; board: AsyncState<Board>; onProjectChanged: () => void }) {
  const { t } = useI18n();
  const { toast, announce } = useToast();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({ search: "", risk: "", type: "", origin: "", prd: "" });
  const [busyTask, setBusyTask] = useState<string | null>(null);
  // A local receipt of an accepted start request; the worker's attempt is the execution evidence.
  const [pendingStarts, setPendingStarts] = useState<Set<string>>(() => new Set());
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);
  const [dragging, setDragging] = useState<TaskCard | null>(null);
  const [confirm, setConfirm] = useState<{ task: TaskCard; action: TaskAction } | null>(null);
  const [reason, setReason] = useState("");
  const [creating, setCreating] = useState(false);
  // Cards rendered per column/side group; "Load +N/remaining" reveals the next page (client-side).
  const [visible, setVisible] = useState<Record<string, number>>({});
  const shownFor = (key: string) => visible[key] ?? KANBAN_PAGE_SIZE;
  const loadMore = (key: string) => setVisible((v) => ({ ...v, [key]: (v[key] ?? KANBAN_PAGE_SIZE) + KANBAN_PAGE_SIZE }));
  const loadMoreButton = (key: string, total: number) => {
    const remaining = total - shownFor(key);
    if (remaining <= 0) return null;
    return (
      <Button variant="ghost" className="kanban-load-more" onClick={() => loadMore(key)} data-testid={`load-more-${key}`}>
        {t("kanban.loadMore", { count: Math.min(KANBAN_PAGE_SIZE, remaining), remaining })}
      </Button>
    );
  };
  // Server-computed available actions (GET /v1/tasks/{id}), keyed by task id + version.
  const [serverActions, setServerActions] = useState<Record<string, TaskAction[]>>({});
  const loadActions = (task: TaskCard) => {
    const key = `${task.id}:${task.version}`;
    if (serverActions[key]) return;
    getTask(task.id)
      .then((full) => {
        if (Array.isArray(full.available_actions)) setServerActions((m) => ({ ...m, [key]: full.available_actions as TaskAction[] }));
      })
      .catch(() => undefined);
  };
  const actionsFor = (task: TaskCard) => serverActions[`${task.id}:${task.version}`] ?? null;

  // Attempt/model events refresh the cards in place (debounced: model calls come in bursts).
  const refreshCards = useDebouncedCallback(board.reload, 800);
  useRealtimeEvents((e) => {
    if (CARD_REFRESH_EVENTS.has(e.type)) refreshCards();
  }, project.id);

  const openTaskId = params.get("task");
  const data = board.data;
  const tasks = useMemo(() => (data ? boardTasks(data) : []), [data]);
  useEffect(() => {
    setPendingStarts((ids) => {
      const remaining = [...ids].filter((id) => tasks.some((task) => task.id === id && task.status === "todo" && !task.current_attempt));
      return remaining.length === ids.size ? ids : new Set(remaining);
    });
  }, [tasks]);
  const numbers = useMemo(() => new Map(tasks.map((x) => [x.id, x.number])), [tasks]);
  const prdVersions = useMemo(() => [...new Set(tasks.map((x) => x.prd_version_id).filter((v): v is string => Boolean(v)))], [tasks]);
  const openTask = openTaskId ? (tasks.find((x) => x.id === openTaskId) ?? null) : null;
  // Dependents of the task being reopened that already started (the confirmation lists them; they are not paused).
  const reopenDependents = confirm?.action === "reopen" ? startedDependents(tasks, [confirm.task.id]) : [];

  const setOpenTask = (task: TaskCard | null) => {
    const next = new URLSearchParams(params);
    if (task) next.set("task", task.id);
    else next.delete("task");
    setParams(next, { replace: true });
  };

  const request = async (task: TaskCard, action: TaskAction, why?: string) => {
    setBusyTask(task.id);
    setRefusal(null);
    try {
      const updated = await taskAction(task.id, { action, expected_version: task.version, ...(why ? { reason: why } : {}) });
      const pending = action === "start" && task.type !== "investigation" && updated?.status === "todo";
      if (pending) setPendingStarts((ids) => new Set(ids).add(task.id));
      const msg = pending ? t("kanban.startAccepted", { number: task.number }) : t("kanban.accepted", { number: task.number, status: t(statusView(updated?.status ?? task.status).label) });
      toast(msg, "success");
      announce(msg);
      board.reload();
    } catch (e) {
      const code = isApiError(e) ? e.code : "unknown";
      const r: Refusal = {
        taskNumber: task.number,
        action,
        code,
        message: localErrorMessage(e, t),
        pendingGates: isApiError(e) ? pendingGatesOf(e.details) : [],
        correlationId: isApiError(e) ? e.correlationId : null,
      };
      setRefusal(r);
      announce(t("kanban.refusedAnnounce", { number: task.number, reason: r.message }));
      // A refused mutation can still commit a safe compensation on the server.
      board.reload();
    } finally {
      setBusyTask(null);
    }
  };

  const onAction = (task: TaskCard, action: TaskAction) => {
    // Reopening a task closed as already existing warns its started dependents (owner answer 7b: no pause).
    if (action === "cancel" || action === "fail" || (action === "reopen" && task.completion_kind === "existing")) {
      setReason("");
      setConfirm({ task, action });
      return;
    }
    void request(task, action);
  };

  const onDragStart = (task: TaskCard, e: DragEvent<HTMLElement>) => {
    setDragging(task);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id);
  };

  const dropProps = (status: TaskStatus) => ({
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!dragging) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOver !== status) setDragOver(status);
    },
    onDragLeave: () => setDragOver((cur) => (cur === status ? null : cur)),
    onDrop: (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      setDragOver(null);
      const task = dragging ?? tasks.find((x) => x.id === e.dataTransfer.getData("text/plain")) ?? null;
      setDragging(null);
      if (!task || task.status === status) return;
      const action = actionForMove(task, status);
      if (!action) {
        setRefusal({
          taskNumber: task.number,
          action: "admin_override",
          code: "no_action",
          message: t("kanban.noActionForMove", { from: t(statusView(task.status).label), to: t(TASK_STATUS_VIEW[status].label) }),
          pendingGates: [],
          correlationId: null,
        });
        return;
      }
      onAction(task, action);
    },
  });

  if (board.loading) return <LoadingBlock label={t("common.loading")} lines={6} />;
  if (board.error && !data) return <ErrorBanner error={board.error} onRetry={board.reload} />;
  if (!data) return null;

  const filtered = (list: TaskCard[]) => list.filter((x) => matches(x, filters));
  // D23: tasks waiting for a person get their own column, rendered only while there is one (the council avoids it).
  const awaiting = filtered(data.side_lane.find((s) => s.state === "awaiting_human")?.tasks ?? []);
  const filtersActive = Object.values(filters).some(Boolean);

  return (
    <div className="kanban-page">
      <div className="kanban-toolbar">
        <label className="search search--sm">
          <SearchIcon size={16} />
          <span className="sr-only">{t("kanban.search")}</span>
          <input className="input" type="search" placeholder={t("kanban.search")} value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
        </label>
        <label className="compact-select">
          <span>{t("task.risk")}</span>
          <select className="select" value={filters.risk} onChange={(e) => setFilters((f) => ({ ...f, risk: e.target.value }))}>
            <option value="">{t("common.all")}</option>
            {RISK_LEVELS.map((r) => (
              <option key={r} value={r}>
                {t(`risk.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="compact-select">
          <span>{t("task.type")}</span>
          <select className="select" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}>
            <option value="">{t("common.all")}</option>
            {TASK_TYPES.map((r) => (
              <option key={r} value={r}>
                {t(`taskType.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="compact-select">
          <span>{t("task.origin")}</span>
          <select className="select" value={filters.origin} onChange={(e) => setFilters((f) => ({ ...f, origin: e.target.value }))}>
            <option value="">{t("common.all")}</option>
            {TASK_ORIGINS.map((r) => (
              <option key={r} value={r}>
                {t(`origin.${r}`)}
              </option>
            ))}
          </select>
        </label>
        {prdVersions.length > 0 ? (
          <label className="compact-select">
            <span>{t("task.prd")}</span>
            <select className="select" value={filters.prd} onChange={(e) => setFilters((f) => ({ ...f, prd: e.target.value }))}>
              <option value="">{t("common.all")}</option>
              {prdVersions.map((v, i) => (
                <option key={v} value={v}>
                  {t("kanban.prdVersionOption", { n: i + 1 })}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {filtersActive ? (
          <Button size="sm" variant="ghost" onClick={() => setFilters({ search: "", risk: "", type: "", origin: "", prd: "" })}>
            {t("projects.clearFilters")}
          </Button>
        ) : null}
        <span className="spacer" />
        <TranslateTasks projectId={project.id} onTranslated={board.reload} />
        <Button size="sm" variant="primary" icon={<PlusIcon size={14} />} onClick={() => setCreating(true)}>
          {t("kanban.newTask")}
        </Button>
      </div>

      <p className="field-hint kanban-hint">{t("kanban.hint")}</p>

      <div aria-live="polite">
        {refusal ? (
          <Banner
            tone={refusal.code === "no_action" ? "warning" : "error"}
            role="alert"
            title={refusal.code === "no_action" ? t("kanban.moveNotPossible", { number: refusal.taskNumber }) : t("kanban.refused", { number: refusal.taskNumber, action: t(`action.${refusal.action}`) })}
            meta={
              <>
                {refusal.code !== "no_action" ? <span className="mono">{refusal.code}</span> : null}
                {refusal.correlationId ? <span> · {t("error.correlation", { id: refusal.correlationId })}</span> : null}
              </>
            }
            action={
              <Button size="sm" variant="ghost" onClick={() => setRefusal(null)}>
                {t("common.dismiss")}
              </Button>
            }
          >
            <p data-testid="refusal-message">{refusal.message}</p>
            {refusal.pendingGates.length > 0 ? (
              <p>
                {t("kanban.pendingGates")}{" "}
                {refusal.pendingGates.map((g) => (
                  <span key={g} className="badge mono" style={{ marginRight: 4 }}>
                    {g}
                  </span>
                ))}
              </p>
            ) : null}
          </Banner>
        ) : null}
      </div>

      {tasks.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<KanbanIcon />}
            title={t("kanban.emptyTitle")}
            actions={
              <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setCreating(true)}>
                {t("kanban.newTask")}
              </Button>
            }
          >
            <p>{t("kanban.emptyBody")}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="kanban-layout">
          <div className="kanban-scroll" role="region" aria-label={t("kanban.board")} tabIndex={0}>
            <ol className={`kanban${awaiting.length > 0 ? " kanban--with-human" : ""}`}>
              {awaiting.length > 0 ? <AwaitingHumanColumn projectId={project.id} tasks={awaiting} onOpen={setOpenTask} /> : null}
              {TASK_COLUMNS.map((col) => {
                const column = data.columns.find((c) => c.column === col);
                const list = filtered(column?.tasks ?? []);
                return (
                  <li key={col} className={`kanban-column${dragOver === col ? " is-drop-target" : ""}`} {...dropProps(col)} data-column={col} aria-labelledby={`col-${col}`}>
                    <h3 id={`col-${col}`} className="kanban-column-title">
                      <StatusIcon kind={TASK_STATUS_VIEW[col].icon} />
                      {t(TASK_STATUS_VIEW[col].label)}
                      <span className="count">{list.length}</span>
                    </h3>
                    <ul className="kanban-cards">
                      {list.slice(0, shownFor(col)).map((task) => (
                        <BoardCard key={task.id} task={task} numbers={numbers} busy={busyTask === task.id} onAction={onAction} onOpen={setOpenTask} onDragStart={onDragStart} startPending={pendingStarts.has(task.id)} serverActions={actionsFor(task)} onMenuOpen={loadActions} onChanged={board.reload} />
                      ))}
                    </ul>
                    {loadMoreButton(col, list.length)}
                  </li>
                );
              })}
            </ol>
          </div>
          <aside className="side-lane" aria-label={t("kanban.sideLane")}>
            <h3 className="kanban-column-title">{t("kanban.sideLane")}</h3>
            {TASK_SIDE_STATES.filter((s) => s !== "awaiting_human").map((state) => {
              const group = data.side_lane.find((s) => s.state === state);
              const list = filtered(group?.tasks ?? []);
              return (
                <section key={state} className={`side-group${dragOver === state ? " is-drop-target" : ""}`} {...dropProps(state)} data-column={state} aria-labelledby={`side-${state}`}>
                  <h4 id={`side-${state}`} className="side-group-title">
                    <StatusIcon kind={TASK_STATUS_VIEW[state].icon} />
                    {t(TASK_STATUS_VIEW[state].label)}
                    <span className="count">{list.length}</span>
                  </h4>
                  {list.length > 0 ? (
                    <ul className="kanban-cards">
                      {list.slice(0, shownFor(state)).map((task) => (
                        <BoardCard key={task.id} task={task} numbers={numbers} busy={busyTask === task.id} onAction={onAction} onOpen={setOpenTask} onDragStart={onDragStart} startPending={pendingStarts.has(task.id)} serverActions={actionsFor(task)} onMenuOpen={loadActions} onChanged={board.reload} />
                      ))}
                    </ul>
                  ) : null}
                  {loadMoreButton(state, list.length)}
                </section>
              );
            })}
          </aside>
        </div>
      )}

      <TaskDetailDialog task={openTask} project={project} numbers={numbers} onClose={() => setOpenTask(null)} onAction={onAction} onDecided={board.reload} onProjectChanged={onProjectChanged} />

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm ? t("kanban.confirmTitle", { action: t(`action.${confirm.action}`), number: confirm.task.number }) : ""}
        description={confirm?.action === "cancel" ? t("kanban.confirmCancelBody") : confirm?.action === "reopen" ? t("onboarding.reopenExistingBody") : t("kanban.confirmFailBody")}
        footer={
          <>
            <Button onClick={() => setConfirm(null)}>{t("common.back")}</Button>
            <Button
              variant={confirm?.action === "reopen" ? "primary" : "danger"}
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                if (c) void request(c.task, c.action, reason.trim() || undefined);
              }}
            >
              {confirm ? t(`action.${confirm.action}`) : ""}
            </Button>
          </>
        }
      >
        <StartedDependentsList tasks={reopenDependents} />
        <TextAreaField label={t("kanban.reason")} optionalLabel={t("common.optional")} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Dialog>

      <CreateTaskDialog open={creating} projectId={project.id} onClose={() => setCreating(false)} onCreated={board.reload} />
    </div>
  );
}
